import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Group, Button, createStyles, Switch, ActionIcon, Divider } from '@mantine/core'
import { isNotEmpty, useForm } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import type { IPlaylist } from "./types";
import Page from "../../components/layout/Page";
import { ISong } from "../song/types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { IconTrash } from "@tabler/icons-react";
import { clone } from "ramda";
import SearchableSelect from "../../components/searchable-select/SearchableSelect";

const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const grid = 8;

const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? "#bcf1b0" : "lightgrey",
    padding: grid,
});

const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    // padding: grid * 2,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? "#eeedee" : "#fff",

    // styles we need to apply on draggables
    ...draggableStyle
});

interface FormValues extends Pick<IPlaylist, 'title' | 'isActive'> {
}

const EditBanner = () => {

    const { state: id = null } = useLocation();

    const { classes } = useStyles();
    const navigate = useNavigate();

    const [ songs, setSongs ] = React.useState<ISong[]>([]);

    const form = useForm<FormValues>({
        initialValues: {
            title: '',
            isActive: false
        },

        validate: {
            title: isNotEmpty('Нэр оруулна уу'),
        },
    });

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<IPlaylist>>(`${API_ROUTE}/${id}`);
            setSongs(data.result.songs);
            form.setValues(data.result)
            form.resetDirty();
        }
        void fetchData();
    }, []);

    const handleSubmit = async (values: typeof form.values) => {
        const valuesClone = clone(values);
        // @ts-ignore
        delete valuesClone.songIds;
        // @ts-ignore
        delete valuesClone.songs;
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, valuesClone);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    const parser = (song: ISong) => {
        return {
            value: song._id,
            label: `${song.singer} - ${song.title}`,
        }
    }

    const updateSongs = async () => {
        const { data } = await axios.get<IResponse<IPlaylist>>(`${API_ROUTE}/${id}`);
        setSongs(data.result.songs);
    }

    const addSong = async (songId: string) => {
        const { data } = await axios.post(`/api/playlist/${id}/song/${songId}`);
        if (data.statusCode === 200) {
            void updateSongs();
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    const removeSong = async (songId: string) => {
        const { data } = await axios.delete(`/api/playlist/${id}/song/${songId}`);
        if (data.statusCode === 200) {
            void updateSongs();
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    const handleDragEnd = async ({ destination, source }: any) => {
        if (!destination) {
            return;
        }

        const songIds = reorder(
            songs,
            source.index,
            destination.index
        ).map((song: any) => song._id);

        const params = {
            songIds
        }

        const { data } = await axios.post(`/api/playlist/${id}/change-order`, params);

        if (data.statusCode === 200) {
            void updateSongs();
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    };

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} засах`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ maxWidth: 500 }} p={'1rem'}>
                    <form onSubmit={form.onSubmit(handleSubmit)}>
                        <TextInput
                            withAsterisk
                            label="Нэр"
                            placeholder="Нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <Switch
                            mt='sm'
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                        <Group position="right" mt="md">
                            <Button color='color-secondary' type='submit'>Хадгалах</Button>
                        </Group>
                    </form>
                </Box>
                <Divider my="xs" mx='1rem'/>
                <div className={classes.container}>
                    <div className={classes.search}>
                        <SearchableSelect<ISong>
                            uri={'/api/song/search'}
                            parser={parser}
                            onChange={(value) => {
                                if (value) {
                                    void addSong(value);
                                }
                            }}
                        />
                    </div>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>Код 1</th>
                                <th>Дуучин</th>
                                <th>Дууны нэр</th>
                                <th/>
                            </tr>
                        </thead>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided, snapshot) => (
                                    <tbody
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={getListStyle(snapshot.isDraggingOver)}
                                    >
                                    {songs.map((song, index) => (
                                        <Draggable key={song._id} draggableId={song._id} index={index}>
                                            {(provided, snapshot) => (
                                                <tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                >
                                                    <td>{index + 1}</td>
                                                    <td>{song.code1}</td>
                                                    <td>{song.singer}</td>
                                                    <td>{song.title}</td>
                                                    <td>
                                                        <ActionIcon
                                                            onClick={() => {
                                                                void removeSong(song._id)
                                                            }}
                                                            color='red'
                                                        >
                                                            <IconTrash size="1.125rem" />
                                                        </ActionIcon>
                                                    </td>
                                                </tr>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </table>
                </div>
            </Page.Body>
            <Page.Footer>

            </Page.Footer>
        </Page>
    );
};

export default EditBanner;

const useStyles = createStyles((theme) => {
    return {
        container: {
            padding: '1rem'
        },
        search: {
            width: 400
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '1rem 0',
            'th, td': {
                textAlign: 'left',
                border: '1px solid',
                borderColor: theme.colors.gray[2],
                padding: '.3rem 1rem'
            }
        }
    }
});